import { Autocomplete, Grid, TextField } from "@mui/material";
import { FormikTextField, useResizableFieldMaxHeight } from "@shared";

import { SmsTemplateResponse } from "@/api";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useTranslation } from "@/translations";

import { useSmsSend } from "./hooks/useSmsSend";

interface Props {
  clientId: string;
  onClose: () => void;
  initialTemplateTitle?: string;
}

const SMS_SEGMENT_LENGTH = 160;

export function SmsSendDialog({ clientId, onClose, initialTemplateTitle }: Props) {
  const { t } = useTranslation();
  const { formik, phoneOptions, templates, applyTemplate, selectedTemplate } = useSmsSend(
    clientId,
    onClose,
    initialTemplateTitle
  );
  const { setFieldRef, maxHeight } = useResizableFieldMaxHeight();

  const messageLength = formik.values.messageContent.length;

  const messageHelperText =
    messageLength > SMS_SEGMENT_LENGTH
      ? t("clients.client.sms.dialog.textLengthWithSegments", {
          length: messageLength,
          segments: Math.ceil(messageLength / SMS_SEGMENT_LENGTH),
        })
      : t("clients.client.sms.dialog.textLength", { length: messageLength });

  return (
    <FormDialog
      title={t("clients.client.sms.dialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("clients.client.sms.dialog.send")}
    >
      <Grid container spacing={3} sx={{ pt: 1, minWidth: "500px" }}>
        <Grid size={12}>
          <FormikAutocomplete
            formik={formik}
            fieldName="recipientNumber"
            label={t("clients.client.sms.dialog.recipientNumber")}
            options={phoneOptions}
            freeSolo
            required
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete<SmsTemplateResponse>
            options={templates}
            value={selectedTemplate}
            // Locked when opened for a specific purpose (e.g. upload activation) and its template was
            // actually applied. If the preset template isn't found (e.g. not seeded in this environment),
            // the selector stays enabled so the user can still pick one manually.
            disabled={initialTemplateTitle !== undefined && selectedTemplate !== null}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, selected) => applyTemplate(selected)}
            noOptionsText={t("settings.templates.noTemplates")}
            renderInput={(params) => (
              <TextField {...params} label={t("clients.client.sms.dialog.selectTemplate")} size="small" />
            )}
          />
        </Grid>
        <Grid size={12}>
          <FormikTextField
            formik={formik}
            fieldName="messageContent"
            label={t("clients.client.sms.dialog.messageContent")}
            required
            multiline
            rows={6}
            helperText={messageHelperText}
            inputRef={setFieldRef}
            sx={{
              "& .MuiInputBase-inputMultiline": { resize: "vertical", maxHeight },
            }}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
}
